/**
 * Notifications API endpoints - preferences, briefings, reminders
 */
import { fetchApi } from './apiBase';

// === Types ===

export interface NotificationPreferences {
  // Field names are the backend serializer's exactly
  // (`backend/notifications/serializers.py`, NotificationPreferenceSerializer).
  morning_briefing_enabled: boolean;
  morning_briefing_time: string;
  morning_briefing_email?: boolean;
  leave_reminder_enabled: boolean;
  leave_reminder_minutes: number;
  change_alert_enabled: boolean;
  change_alert_email?: boolean;
  conflict_alert_enabled: boolean;
  plan_digest_enabled: boolean;
  plan_digest_time?: string;
  /** The Explore publish-outcome courtesy email (increment 5). */
  publish_outcome_email: boolean;
  lock_warning_enabled: boolean;
  timezone?: string;
  onesignal_player_id?: string;
}

export interface Notification {
  id: string;
  type: string;
  notification_type?: string;
  title: string;
  message: string;
  subject?: string;
  body?: string;
  is_read: boolean;
  status?: string;
  created_at: string;
  data?: Record<string, unknown>;
}

export interface MorningBriefingEventData {
  id: string;
  title: string;
  start_time: string;
  end_time?: string;
  location?: string;
  event_type?: string;
  all_day?: boolean;
}

export interface MorningBriefingPreview {
  subject: string;
  text: string;
  html: string;
  events_count: number;
  events_data: {
    members: Record<string, { name: string; role: string; events: MorningBriefingEventData[] }>;
    shared: MorningBriefingEventData[];
    total: number;
  };
}

export interface ReminderData {
  id: string;
  text: string;
  notes?: string;
  category: 'general' | 'school' | 'activity' | 'appointment' | 'household' | 'travel' | 'birthday';
  priority: 'low' | 'normal' | 'high';
  due_date: string | null;
  show_from_date?: string | null;
  family_member?: string | null;
  family_member_name?: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  event?: string | null;
  event_title?: string | null;
  is_completed: boolean;
  completed_at?: string | null;
  is_overdue?: boolean;
  is_due_soon?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ReminderCreateData {
  text: string;
  notes?: string;
  category?: string;
  priority?: string;
  due_date?: string | null;
  show_from_date?: string | null;
  family_member?: string | null;
  assigned_to?: string | null;
  event?: string | null;
}

// === Notification Preferences ===

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return fetchApi<NotificationPreferences>('/api/notifications/preferences/');
}

export async function updateNotificationPreferences(
  data: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
  return fetchApi<NotificationPreferences>('/api/notifications/preferences/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function testNotification(type: 'email' | 'push'): Promise<{ success: boolean; message: string }> {
  return fetchApi('/api/notifications/test/', {
    method: 'POST',
    body: JSON.stringify({ type }),
  });
}

// === Notifications ===

export async function getNotifications(limit?: number): Promise<{ notifications: Notification[] }> {
  const query = limit ? `?limit=${limit}` : '';
  return fetchApi<{ notifications: Notification[] }>(`/api/notifications/${query}`);
}

export async function markNotificationRead(id: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/notifications/${id}/read/`, { method: 'POST' });
}

// === Morning Briefings ===

export async function previewMorningBriefing(date?: string): Promise<MorningBriefingPreview> {
  const query = date ? `?date=${date}` : '';
  return fetchApi<MorningBriefingPreview>(`/api/notifications/briefings/preview/${query}`);
}

export async function sendMorningBriefing(date?: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  subject?: string;
  events_count?: number;
}> {
  return fetchApi('/api/notifications/briefings/send/', {
    method: 'POST',
    body: JSON.stringify({ date }),
  });
}

// === Reminders ===

export async function getReminders(params?: {
  completed?: boolean;
  category?: string;
  due_date?: string;
  show_for_date?: string;
}): Promise<{ reminders: ReminderData[] }> {
  const searchParams = new URLSearchParams();
  if (params?.completed !== undefined) {
    searchParams.set('completed', params.completed.toString());
  }
  if (params?.category) {
    searchParams.set('category', params.category);
  }
  if (params?.due_date) {
    searchParams.set('due_date', params.due_date);
  }
  if (params?.show_for_date) {
    searchParams.set('show_for_date', params.show_for_date);
  }
  const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return fetchApi<{ reminders: ReminderData[] }>(`/auth/reminders/${query}`);
}

export async function createReminder(data: ReminderCreateData): Promise<ReminderData> {
  return fetchApi<ReminderData>('/auth/reminders/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateReminder(id: string, data: Partial<ReminderCreateData>): Promise<ReminderData> {
  return fetchApi<ReminderData>(`/auth/reminders/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteReminder(id: string): Promise<void> {
  await fetchApi(`/auth/reminders/${id}/`, { method: 'DELETE' });
}

export async function completeReminder(id: string): Promise<ReminderData> {
  return fetchApi<ReminderData>(`/auth/reminders/${id}/complete/`, { method: 'POST' });
}

export async function uncompleteReminder(id: string): Promise<ReminderData> {
  return fetchApi<ReminderData>(`/auth/reminders/${id}/uncomplete/`, { method: 'POST' });
}
